import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HiOutlineShoppingBag,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineCube,
  HiOutlineHashtag,
  HiOutlineMapPin,
} from "react-icons/hi2";
import {
  ShieldCheck,
  Building2,
  Package,
  Heart,
  Repeat,
  Eye,
  Truck,
  Clock,
  CheckCircle2,
} from "lucide-react";
import SummaryApi from "../common";
import toast from "react-hot-toast";
import InvoiceModal from "./InvoiceModal";
import UserProductReview from "./UserProductReview";
import { Star } from "lucide-react";
// Helper to map statuses to steps
const ORDER_STAGES = [
  { label: "Placed", status: ["Order Placed", "success"] },
  { label: "Processing", status: ["Order Confirmed", "under Progress", "PP"] },
  { label: "Shipped", status: ["Order Shipped", "IT"] },
  { label: "Out for Delivery", status: ["OFD"] },
  { label: "Delivered", status: ["Delivered", "DL"] },
];
const MyOrders = () => {
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("activeOrdersTab") || "products";
  });
  const [products, setProducts] = useState([]);
  const [enrichedProducts, setEnrichedProducts] = useState([]);
  const [sakhiOrders, setSakhiOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSakhi, setLoadingSakhi] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [pageByTab, setPageByTab] = useState({
    products: 1,
    sakhi: 1,
    subscriptions: 1,
  });
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [selectedReviewData, setSelectedReviewData] = useState(null);
  const [reviewedItems, setReviewedItems] = useState({});
  const [checkingReviews, setCheckingReviews] = useState(false);
  const itemsPerPage = 3;
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] =
    useState(null);
  const [liveTrackingData, setLiveTrackingData] = useState(null);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  // Refs to track if data has been fetched
  const productsFetched = useRef(false);
  const sakhiFetched = useRef(false);
  const subscriptionsFetched = useRef(false);
  const reviewsChecked = useRef(false);
  const initialDataLoaded = useRef(false);

  const openReviewModal = (item, type, order_id, subscription_id = null) => {
    setSelectedReviewData({
      order_id: order_id,
      product_id: item.product_id || null,
      name: item.product_name || item.plan_name || "Order",
      type: type,
      user_pack_subscription_id: subscription_id,
    });
    setIsReviewOpen(true);
  };

  const handleReviewSubmitted = useCallback(() => {
    // Reset the reviews checked flag so it will check again after submission
    reviewsChecked.current = false;
    // Force a re-check of reviews
    setTimeout(() => {
      checkItemReviews(true); // Pass true to force refresh
    }, 500);
  }, []);
  const handleTrackOrder = async (order) => {
    setSelectedOrderForTracking(order);
    setIsTrackModalOpen(true);
    setLiveTrackingData(null);

    if (order.tracking_number) {
      setIsTrackingLoading(true);
      try {
        const response = await fetch(SummaryApi.trackOrderById.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tracking_number: order.tracking_number }),
        });
        const result = await response.json();
        if (result.success || result.status) {
          setLiveTrackingData(result);
        }
      } catch (err) {
        console.error("Tracking fetch error", err);
      } finally {
        setIsTrackingLoading(false);
      }
    }
  };
  // Locate this function in your code and replace it:
  const getCurrentStepIndex = (orderStatus, trackingStatus) => {
    // Ensure we are working with strings to avoid .toUpperCase() errors
    const orderStr = String(orderStatus || "").toUpperCase();
    const trackStr = String(trackingStatus || "").toUpperCase();

    // Use tracking status first if it's a valid string status, otherwise use order status
    // We check if it's not "TRUE" because the API status field often returns boolean true
    const current = trackStr && trackStr !== "TRUE" ? trackStr : orderStr;

    if (current.includes("DL") || current.includes("DELIVERED")) return 4;
    if (current.includes("OFD")) return 3;
    if (current.includes("IT") || current.includes("SHIPPED")) return 2;
    if (
      current.includes("CONFIRMED") ||
      current.includes("PROGRESS") ||
      current.includes("PP") ||
      current.includes("BOOKED")
    )
      return 1;
    return 0; // Placed
  };
  const IMAGE_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://localhost:3001";

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed;
    } catch {
      return null;
    }
  }, []);

  const fetchUnitDetails = async (product_unit_id) => {
    if (!product_unit_id) return null;
    try {
      const res = await fetch(
        SummaryApi.product_unit_admin_retrieve_by_id.url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_unit_id }),
        },
      );
      const json = await res.json();
      return json.status && json.data ? json.data : null;
    } catch (err) {
      return null;
    }
  };

  const checkItemReviews = useCallback(
    async (forceRefresh = false) => {
      const userId = getUserId();
      if (!userId) return;

      // Don't check if already checked (unless forced)
      if (reviewsChecked.current && !forceRefresh) return;

      setCheckingReviews(true);
      const reviewedMap = {};

      try {
        // Check product reviews
        for (const order of enrichedProducts) {
          for (const item of order.order_items || []) {
            try {
              const res = await fetch(SummaryApi.getuserrating.url, {
                method: SummaryApi.getuserrating.method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: userId,
                  product_id: item.product_id,
                }),
              });
              const data = await res.json();
              if (data.status && data.data && data.data.length > 0) {
                reviewedMap[`product_${item.product_id}_${order.order_id}`] =
                  true;
              }
            } catch (err) {
              console.error("Error checking product review:", err);
            }
          }
        }

        // Check Sakhi reviews
        for (const order of sakhiOrders) {
          try {
            const res = await fetch(SummaryApi.getusersakhirating.url, {
              method: SummaryApi.getusersakhirating.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                order_id: order.order_id || order.id,
              }),
            });
            const data = await res.json();
            if (data.status && data.data && data.data.length > 0) {
              reviewedMap[`sakhi_${order.order_id || order.id}`] = true;
            }
          } catch (err) {
            console.error("Error checking Sakhi review:", err);
          }
        }

        // Check Subscription reviews
        for (const order of subscriptions) {
          try {
            const res = await fetch(SummaryApi.getusersubscriptionrating.url, {
              method: SummaryApi.getusersubscriptionrating.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                user_pack_subscription_id:
                  order.user_pack_subscription_id || order.id,
              }),
            });
            const data = await res.json();
            if (data.status && data.data && data.data.length > 0) {
              reviewedMap[
                `subscription_${order.user_pack_subscription_id || order.id}`
              ] = true;
            }
          } catch (err) {
            console.error("Error checking subscription review:", err);
          }
        }

        setReviewedItems(reviewedMap);
        reviewsChecked.current = true;

        // Log for debugging
        console.log("Reviewed items updated:", reviewedMap);
      } catch (error) {
        console.error("Error checking reviews:", error);
      } finally {
        setCheckingReviews(false);
      }
    },
    [enrichedProducts, sakhiOrders, subscriptions, getUserId],
  );

  const fetchProducts = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    // Don't fetch if already fetched
    if (productsFetched.current) return;

    setLoadingProducts(true);
    try {
      const res = await fetch(SummaryApi.getOrdersById.url, {
        method: SummaryApi.getOrdersById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const json = await res.json();
      if (json.status) {
        const productOrders = json.data.filter(
          (o) => o.is_sakhi === 0 && o.is_subscription !== 1,
        );
        const enriched = await Promise.all(
          productOrders.map(async (order) => {
            if (!order.order_items?.length) return order;
            const itemsWithUnit = await Promise.all(
              order.order_items.map(async (item) => {
                const unit = await fetchUnitDetails(item.product_unit_id);
                return { ...item, unit };
              }),
            );
            return { ...order, order_items: itemsWithUnit };
          }),
        );
        setEnrichedProducts(enriched);
        setProducts(productOrders);
        productsFetched.current = true;
      }
    } catch (err) {
      toast.error("Failed to load product orders");
    } finally {
      setLoadingProducts(false);
    }
  }, [getUserId]);

  const fetchSakhi = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    // Don't fetch if already fetched
    if (sakhiFetched.current) return;

    setLoadingSakhi(true);
    try {
      const res = await fetch(SummaryApi.getSakhiOrdersById.url, {
        method: SummaryApi.getSakhiOrdersById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const json = await res.json();
      if (json.status) {
        setSakhiOrders(json.data || []);
        sakhiFetched.current = true;
      }
    } catch (err) {
      toast.error("Failed to load sponsorship orders");
    } finally {
      setLoadingSakhi(false);
    }
  }, [getUserId]);

  const fetchSubscriptions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    // Don't fetch if already fetched
    if (subscriptionsFetched.current) return;

    setLoadingSubscriptions(true);
    try {
      const res = await fetch(SummaryApi.subscription_order_by_user.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const json = await res.json();
      if (json.status && json.data) {
        setSubscriptions(json.data);
        subscriptionsFetched.current = true;
      }
    } catch (err) {
      toast.error("Failed to load subscriptions");
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [getUserId]);

  // Reset fetch flags when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    sessionStorage.setItem("activeOrdersTab", tab);

    setPageByTab((prev) => ({ ...prev, [tab]: 1 }));

    if (tab === "products") {
      productsFetched.current = false;
    } else if (tab === "sakhi") {
      sakhiFetched.current = false;
    } else if (tab === "subscriptions") {
      subscriptionsFetched.current = false;
    }

    reviewsChecked.current = false;
  };

  // Fetch data when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);

    if (activeTab === "products") {
      fetchProducts();
    } else if (activeTab === "sakhi") {
      fetchSakhi();
    } else if (activeTab === "subscriptions") {
      fetchSubscriptions();
    }
  }, [activeTab, fetchProducts, fetchSakhi, fetchSubscriptions]);

  // Check reviews after data is loaded - only once
  useEffect(() => {
    const hasData =
      enrichedProducts.length > 0 ||
      sakhiOrders.length > 0 ||
      subscriptions.length > 0;

    if (hasData && !reviewsChecked.current) {
      checkItemReviews();
    }
  }, [enrichedProducts, sakhiOrders, subscriptions, checkItemReviews]);

  // Reset flags when component unmounts
  useEffect(() => {
    return () => {
      productsFetched.current = false;
      sakhiFetched.current = false;
      subscriptionsFetched.current = false;
      reviewsChecked.current = false;
    };
  }, []);

  const getTabConfig = () => {
    switch (activeTab) {
      case "products":
        return {
          data: enrichedProducts,
          isLoading: loadingProducts,
          title: "My Box Orders",
          icon: HiOutlineCube,
          color: "rose",
        };
      case "sakhi":
        return {
          data: sakhiOrders,
          isLoading: loadingSakhi,
          title: "Sakhi Impact",
          icon: Heart,
          color: "purple",
        };
      case "subscriptions":
        return {
          data: subscriptions,
          isLoading: loadingSubscriptions,
          title: "My Subscriptions",
          icon: Repeat,
          color: "indigo",
        };
      default:
        return {
          data: [],
          isLoading: false,
          title: "",
          icon: HiOutlineCube,
          color: "rose",
        };
    }
  };

  const {
    data: displayData,
    isLoading,
    title,
    icon: TabIcon,
    color,
  } = getTabConfig();

  // Pagination logic
  const currentPage = pageByTab[activeTab] || 1;
  const totalPages = Math.ceil(displayData.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = displayData.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setPageByTab((prev) => ({ ...prev, [activeTab]: page }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getStatusColor = (status = "") => {
    const s = status.toLowerCase();
    if (s.includes("placed") || s.includes("success") || s.includes("active"))
      return "bg-green-100 text-green-700";
    if (s.includes("delivered") || s.includes("completed"))
      return "bg-blue-100 text-blue-700";
    if (s.includes("cancel") || s.includes("inactive") || s.includes("failed"))
      return "bg-rose-100 text-rose-700";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="min-h-screen bg-[#FFF0F3] py-14 px-5 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Tabs with Counts */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-4">
            <div
              className={`h-12 w-12 rounded-2xl flex items-center justify-center text-white shadow-lg bg-${color}-500 shadow-${color}-200`}
            >
              <TabIcon size={24} />
            </div>
            <h1 className="text-4xl font-black text-rose-950 uppercase tracking-tight">
              {title}
            </h1>
          </div>
          <div className="bg-white/50 p-1.5 rounded-2xl flex border border-white/50 shadow-sm flex-wrap gap-2">
            <button
              onClick={() => handleTabChange("products")}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === "products"
                  ? "bg-rose-500 text-white shadow-md"
                  : "text-rose-900/60 hover:text-rose-600"
              }`}
            >
              Products
              {enrichedProducts.length > 0
                ? ` (${enrichedProducts.length})`
                : ""}
            </button>
            {/* <button
              onClick={() => handleTabChange("sakhi")}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === "sakhi"
                  ? "bg-purple-700 text-white shadow-md"
                  : "text-rose-900/60 hover:text-purple-700"
              }`}
            >
              Sponsorships
              {sakhiOrders.length > 0 ? ` (${sakhiOrders.length})` : ""}
            </button> */}
            <button
              onClick={() => handleTabChange("subscriptions")}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === "subscriptions"
                  ? "bg-indigo-600 text-white shadow-md"
                  : "text-rose-900/60 hover:text-indigo-600"
              }`}
            >
              Subscriptions
              {subscriptions.length > 0 ? ` (${subscriptions.length})` : ""}
            </button>
          </div>
        </div>

        {isLoading || checkingReviews ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white/40 rounded-[3rem]">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-200 border-t-rose-500 mb-4"></div>
            <p className="text-rose-800 font-bold uppercase tracking-widest text-sm">
              {isLoading ? `Loading ${title}...` : "Checking reviews..."}
            </p>
          </div>
        ) : displayData.length === 0 ? (
          <div className="text-center py-20 bg-white/40 rounded-[3rem] border-4 border-dashed border-white/60">
            <HiOutlineShoppingBag
              size={80}
              className="mx-auto text-rose-200 mb-6"
            />
            <h2 className="text-2xl font-black text-rose-950 uppercase">
              No {title.toLowerCase()} yet
            </h2>
          </div>
        ) : (
          <>
            <div className="space-y-5">
              <AnimatePresence mode="wait">
                {currentItems.map((order) => (
                  <motion.div
                    key={order.order_id || order.id || Math.random()}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`bg-white/70 backdrop-blur-md border border-white rounded-[2rem] overflow-hidden shadow-xl ${
                      activeTab === "sakhi"
                        ? "border-l-8 border-l-purple-700"
                        : activeTab === "subscriptions"
                          ? "border-l-8 border-l-indigo-600"
                          : ""
                    }`}
                  >
                    {/* Order Header */}
                    <div className="p-5 md:p-6 border-b border-rose-50 flex flex-wrap justify-between items-center gap-4">
                      <div className="flex gap-5 flex-wrap">
                        <div>
                          <p className="text-[9px] font-black text-rose-900/40 uppercase mb-1">
                            ID
                          </p>
                          <p className="text-xs font-black text-rose-950 flex items-center gap-1 uppercase">
                            <HiOutlineHashtag
                              size={14}
                              className={
                                activeTab === "sakhi"
                                  ? "text-purple-500"
                                  : activeTab === "subscriptions"
                                    ? "text-indigo-500"
                                    : "text-rose-400"
                              }
                            />
                            {order.order_code ||
                              order.subscription_code ||
                              `ORD-${order.order_id}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-rose-900/40 uppercase mb-1">
                            Date
                          </p>
                          <p className="text-xs font-bold text-rose-950">
                            {new Date(
                              order.order_date_time ||
                                order.created_date ||
                                order.subscription_date ||
                                order.order_date,
                            ).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${getStatusColor(order.order_status || (order.is_active ? "Active" : "Inactive") || order.status)}`}
                        >
                          {order.order_status ||
                            (order.is_active ? "Active" : "Inactive") ||
                            order.status ||
                            "Pending"}
                        </span>
                        <p
                          className={`text-xl font-black ${activeTab === "sakhi" ? "text-purple-800" : activeTab === "subscriptions" ? "text-indigo-700" : "text-rose-600"}`}
                        >
                          ₹
                          {order.total_amount ||
                            order.order_amount ||
                            order.amount ||
                            "0.00"}
                        </p>
                      </div>
                    </div>

                    {/* Order Content */}
                    <div className="p-5 md:p-6">
                      {activeTab === "products" && (
                        <div className="space-y-3">
                          {order.order_items?.map((item) => {
                            const imgUrl = `${IMAGE_BASE_URL}/${item.product_img}`;
                            const hasReview =
                              reviewedItems[
                                `product_${item.product_id}_${order.order_id}`
                              ];

                            return (
                              <div
                                key={item.order_items_id}
                                className="flex items-center gap-4 bg-white/60 p-3 rounded-2xl border border-white shadow-sm"
                              >
                                <div className="w-16 h-16 bg-white rounded-xl border border-rose-50 p-1.5 shrink-0">
                                  <img
                                    src={imgUrl}
                                    className="w-full h-full object-contain"
                                    alt={item.product_name}
                                    onError={(e) =>
                                      (e.target.src = "/fallback-product.png")
                                    }
                                  />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-black text-rose-950 text-base uppercase tracking-tight">
                                    {item.product_name}
                                  </h4>
                                  {item.unit && item.unit.pack_size && (
                                    <span className="text-[10px] font-black bg-rose-100 text-rose-700 px-2.5 py-1 rounded-lg mt-1 inline-block">
                                      Pack of {item.unit.pack_size}
                                    </span>
                                  )}
                                  <div className="flex gap-3 mt-2">
                                    <span className="text-[10px] font-black text-rose-500 bg-rose-50 px-2.5 py-1 rounded-lg">
                                      Qty: {item.quantity}
                                    </span>
                                    <span className="text-[10px] font-black text-slate-500 px-2.5 py-1">
                                      ₹{item.price}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <button
                                    onClick={() =>
                                      openReviewModal(
                                        item,
                                        "product",
                                        order.order_id,
                                      )
                                    }
                                    className={`flex items-center gap-1 text-[10px] font-black transition-colors ${
                                      hasReview
                                        ? "text-blue-500 hover:text-blue-700"
                                        : "text-rose-500 hover:text-rose-700"
                                    }`}
                                  >
                                    {hasReview ? (
                                      <>
                                        <Eye size={12} /> VIEW REVIEW
                                      </>
                                    ) : (
                                      <>
                                        <Star size={12} fill="currentColor" />{" "}
                                        ADD REVIEW
                                      </>
                                    )}
                                  </button>
                                </div>
                                <Link
                                  to={`/product/${item.product_id}`}
                                  className="w-10 h-10 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-500 hover:text-white transition-colors"
                                >
                                  <HiOutlineChevronRight size={18} />
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {activeTab === "sakhi" && (
                        <div className="flex flex-col md:flex-row gap-6 items-center bg-purple-50/40 p-6 rounded-[2rem] border border-purple-100/60">
                          <div className="w-20 h-20 bg-purple-800 rounded-[2rem] flex items-center justify-center text-white shadow-xl shrink-0">
                            <ShieldCheck size={36} />
                          </div>
                          <div className="flex-1 text-center md:text-left">
                            <h3 className="text-2xl font-black text-purple-950 uppercase mb-3">
                              {order.plan_name || "Impact"}{" "}
                              <span className="text-pink-500 italic">Plan</span>
                            </h3>
                            <div className="flex flex-wrap justify-center md:justify-start gap-3">
                              <div className="flex items-center gap-1.5 bg-white px-4 py-2 rounded-xl border border-purple-100 shadow-sm">
                                <Heart
                                  size={16}
                                  className="text-pink-500"
                                  fill="currentColor"
                                />
                                <span className="text-sm font-black text-purple-900">
                                  {order.pads_count} Pads
                                </span>
                              </div>
                            </div>
                            {(() => {
                              const hasReview =
                                reviewedItems[
                                  `sakhi_${order.order_id || order.id}`
                                ];
                              return (
                                <button
                                  onClick={() =>
                                    openReviewModal(
                                      order,
                                      "sakhi",
                                      order.order_id || order.id,
                                    )
                                  }
                                  className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all shadow-sm ${
                                    hasReview
                                      ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-700 hover:text-white"
                                      : "bg-white text-purple-700 border border-purple-200 hover:bg-purple-700 hover:text-white"
                                  }`}
                                >
                                  {hasReview ? (
                                    <>
                                      <Eye size={14} /> VIEW IMPACT REVIEW
                                    </>
                                  ) : (
                                    <>
                                      <Star size={14} fill="currentColor" /> ADD
                                      IMPACT REVIEW
                                    </>
                                  )}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {activeTab === "subscriptions" && (
                        <div className="bg-indigo-50/50 p-5 rounded-2xl border border-indigo-100 space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="bg-white/80 p-3 rounded-xl border border-indigo-50">
                              <p className="text-xs text-indigo-600 font-black uppercase mb-0.5">
                                Plan
                              </p>
                              <p className="text-base font-bold text-indigo-950">
                                {order.plan_name || "Subscription Plan"}
                              </p>
                            </div>
                            <div className="bg-white/80 p-3 rounded-xl border border-indigo-50">
                              <p className="text-xs text-indigo-600 font-black uppercase mb-0.5">
                                Pads
                              </p>
                              <p className="text-xl font-black text-indigo-700">
                                {order.pads_count || "?"}
                              </p>
                            </div>
                            <div className="flex justify-end">
                              {(() => {
                                const hasReview =
                                  reviewedItems[
                                    `subscription_${order.user_pack_subscription_id || order.id}`
                                  ];
                                return (
                                  <button
                                    onClick={() =>
                                      openReviewModal(
                                        order,
                                        "subscription",
                                        order.order_id || order.id,
                                        order.user_pack_subscription_id ||
                                          order.id,
                                      )
                                    }
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all shadow-sm ${
                                      hasReview
                                        ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-700 hover:text-white"
                                        : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white"
                                    }`}
                                  >
                                    {hasReview ? (
                                      <>
                                        <Eye size={14} /> VIEW REVIEW
                                      </>
                                    ) : (
                                      <>
                                        <Star size={14} fill="currentColor" />{" "}
                                        ADD REVIEW
                                      </>
                                    )}
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Order Footer */}
                    <div className="px-6 py-3 bg-rose-50/30 border-t border-white flex justify-between items-center">
                      <div className="flex items-center gap-1.5 text-[9px] font-black text-rose-900/50 uppercase tracking-widest">
                        <HiOutlineMapPin
                          size={14}
                          className={
                            activeTab === "sakhi"
                              ? "text-purple-500"
                              : activeTab === "subscriptions"
                                ? "text-indigo-500"
                                : "text-rose-400"
                          }
                        />
                        {order.payment_mode || "Online"}
                      </div>
                      <div className="gap-2">
                        {activeTab === "products" && (
                          <button
                            onClick={() => {
                              setSelectedOrderForInvoice(order);
                              setIsInvoiceOpen(true);
                            }}
                            className="bg-white mr-2 px-5 py-2 rounded-xl border border-rose-100 text-[10px] font-black text-rose-600 uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                          >
                            Get Invoice
                          </button>
                        )}
                        {activeTab === "products" && (
                          <button
                            onClick={() => handleTrackOrder(order)}
                            className="bg-white px-5 py-2 rounded-xl border border-rose-100 text-[10px] font-black text-rose-600 uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                          >
                            Track Order
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination UI */}
            {totalPages > 1 && (
              <div className="mt-10 flex justify-center items-center gap-2 pb-20">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-xl bg-white text-rose-500 flex items-center justify-center disabled:opacity-40 transition-all shadow-md"
                >
                  <HiOutlineChevronLeft size={20} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`min-w-[40px] h-10 px-2 rounded-xl font-black text-sm transition-all shadow-md ${
                      currentPage === i + 1
                        ? `bg-${color === "rose" ? "rose-500" : color === "purple" ? "purple-700" : "indigo-600"} text-white`
                        : "bg-white text-rose-950"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-xl bg-white text-rose-500 flex items-center justify-center disabled:opacity-40 transition-all shadow-md"
                >
                  <HiOutlineChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}
        <AnimatePresence>
          {isTrackModalOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsTrackModalOpen(false)}
                className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm"
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden"
              >
                <div className="bg-rose-500 p-6 text-white flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-black uppercase">
                      Track Your Box
                    </h3>
                    <p className="text-[10px] font-bold opacity-80">
                      ORDER #{selectedOrderForTracking?.order_code}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsTrackModalOpen(false)}
                    className="bg-white/20 hover:bg-white/40 p-2 rounded-full transition-colors"
                  >
                    <HiOutlineChevronRight className="rotate-90" />
                  </button>
                </div>

                <div className="p-8">
                  {isTrackingLoading ? (
                    <div className="flex flex-col items-center py-10">
                      <div className="animate-spin h-10 w-10 border-4 border-rose-100 border-t-rose-500 rounded-full mb-4"></div>
                      <p className="text-xs font-black text-rose-900/40 uppercase">
                        Connecting to Courier...
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Vertical Line */}
                      <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gray-100"></div>

                      {/* Stages */}
                      <div className="space-y-8 relative">
                        {ORDER_STAGES.map((stage, idx) => {
                          const currentIdx = getCurrentStepIndex(
                            selectedOrderForTracking?.order_status,
                            liveTrackingData?.data?.status, // This is the string "booked", "shipped", etc.
                          );
                          const isCompleted = idx <= currentIdx;
                          const isCurrent = idx === currentIdx;

                          return (
                            <div
                              key={stage.label}
                              className="flex items-start gap-6"
                            >
                              <div
                                className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-4 ${
                                  isCompleted
                                    ? "bg-rose-500 border-rose-100 text-white"
                                    : "bg-white border-gray-50 text-gray-300"
                                } transition-all duration-500`}
                              >
                                {isCompleted ? (
                                  <CheckCircle2 size={18} />
                                ) : (
                                  <Clock size={18} />
                                )}
                              </div>
                              <div className="flex-1 pt-1">
                                <h4
                                  className={`text-sm font-black uppercase tracking-tight ${isCompleted ? "text-rose-950" : "text-gray-400"}`}
                                >
                                  {stage.label}
                                </h4>
                                {isCurrent && (
                                  <p className="text-[10px] font-bold text-rose-500 mt-0.5 animate-pulse">
                                    {liveTrackingData?.status_description ||
                                      "Processing your request"}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {selectedOrderForTracking?.tracking_number && (
                        <div className="mt-10 p-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                          <p className="text-[9px] font-black text-gray-400 uppercase mb-1">
                            Courier Tracking ID
                          </p>
                          <p className="text-sm font-black text-rose-950 tracking-widest">
                            {selectedOrderForTracking.tracking_number}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        <InvoiceModal
          order={selectedOrderForInvoice}
          isOpen={isInvoiceOpen}
          onClose={() => setIsInvoiceOpen(false)}
        />

        <UserProductReview
          isOpen={isReviewOpen}
          onClose={() => {
            setIsReviewOpen(false);
            // Don't refresh reviews when modal closes without changes
            // Only refresh if a review was actually submitted
            // The refresh will be handled by onReviewSubmitted callback
          }}
          reviewData={selectedReviewData}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </div>
    </div>
  );
};

export default MyOrders;
